// ===========================================
// Account Deletion Dialog
// ===========================================

import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  TextField,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  DeleteForever as DeleteIcon,
  Error as ErrorIcon,
  EventNote as EventIcon,
  Folder as FolderIcon,
  Notes as NotesIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  School as SchoolIcon,
  Visibility,
  VisibilityOff,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { UseMutationResult } from '@tanstack/react-query';
import type { DeletionStep } from './account.types';

interface DeletionBlocker {
  type: string;
  message: string;
  details?: Record<string, unknown>;
}

interface DeletionSummary {
  dataToDelete: {
    students: number;
    enrollments: number;
    attendanceRecords: number;
    notes: number;
    resources: number;
  };
  dataToAnonymize: {
    invoices: number;
    payments: number;
  };
  gracePeriodDays: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  blockers: DeletionBlocker[];
  canDelete: boolean;
  isLoadingBlockers: boolean;
  summary: DeletionSummary | undefined;
  isLoadingSummary: boolean;
  requestDeletionMutation: UseMutationResult<
    unknown,
    Error,
    { password: string; reason?: string }
  >;
  onDeletionComplete: () => Promise<void>;
}

export default function AccountDeletionDialog({
  open,
  onClose,
  blockers,
  canDelete,
  isLoadingBlockers,
  summary,
  isLoadingSummary,
  requestDeletionMutation,
  onDeletionComplete,
}: Props) {
  const [deletionStep, setDeletionStep] = useState<DeletionStep>('check');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [reason, setReason] = useState('');

  const handleClose = () => {
    setDeletionStep('check');
    setPassword('');
    setReason('');
    setShowPassword(false);
    onClose();
  };

  const handleProceedToSummary = () => setDeletionStep('summary');
  const handleProceedToConfirm = () => setDeletionStep('confirm');

  const handleRequestDeletion = async () => {
    try {
      await requestDeletionMutation.mutateAsync({
        password,
        reason: reason || undefined,
      });
      handleClose();
      await onDeletionComplete();
    } catch {
      // Error handled by mutation
    }
  };

  const getBlockerIcon = (type: string) => {
    switch (type) {
      case 'OUTSTANDING_BALANCE':
        return <ReceiptIcon color="error" />;
      case 'PENDING_BOOKINGS':
        return <EventIcon color="error" />;
      case 'ACTIVE_LESSONS':
        return <SchoolIcon color="error" />;
      case 'ONLY_ADMIN':
        return <PersonIcon color="error" />;
      default:
        return <ErrorIcon color="error" />;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningIcon color="error" />
        Delete Account
      </DialogTitle>

      <DialogContent>
        {deletionStep === 'check' && (
          <Box>
            {isLoadingBlockers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : blockers.length > 0 ? (
              <Alert severity="error">
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Cannot proceed with deletion:
                </Typography>
                <List dense disablePadding>
                  {blockers.map((blocker, index) => (
                    <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {getBlockerIcon(blocker.type)}
                      </ListItemIcon>
                      <ListItemText primary={blocker.message} />
                    </ListItem>
                  ))}
                </List>
              </Alert>
            ) : (
              <Box>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <CheckIcon sx={{ mr: 1 }} />
                  Your account is eligible for deletion.
                </Alert>
                <Typography variant="body2" color="text.secondary">
                  Click "Continue" to review what data will be deleted.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {deletionStep === 'summary' && (
          <Box>
            {isLoadingSummary ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : summary ? (
              <Box>
                <Alert severity="warning" sx={{ mb: 3 }}>
                  The following data will be affected after the 30-day grace period:
                </Alert>

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Data to be permanently deleted:
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Students"
                      secondary={`${summary.dataToDelete.students} student record(s)`}
                    />
                    <Chip label="Deleted" size="small" color="error" variant="outlined" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <SchoolIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Enrolments"
                      secondary={`${summary.dataToDelete.enrollments} enrolment(s)`}
                    />
                    <Chip label="Deleted" size="small" color="error" variant="outlined" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <EventIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Attendance Records"
                      secondary={`${summary.dataToDelete.attendanceRecords} record(s)`}
                    />
                    <Chip label="Deleted" size="small" color="error" variant="outlined" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <NotesIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Notes"
                      secondary={`${summary.dataToDelete.notes} note(s)`}
                    />
                    <Chip label="Deleted" size="small" color="error" variant="outlined" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <FolderIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Resources"
                      secondary={`${summary.dataToDelete.resources} file(s)`}
                    />
                    <Chip label="Deleted" size="small" color="error" variant="outlined" />
                  </ListItem>
                </List>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Data to be anonymized (for legal records):
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <ReceiptIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Invoices"
                      secondary={`${summary.dataToAnonymize.invoices} invoice(s)`}
                    />
                    <Chip label="Anonymized" size="small" color="warning" variant="outlined" />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <ReceiptIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary="Payments"
                      secondary={`${summary.dataToAnonymize.payments} payment(s)`}
                    />
                    <Chip label="Anonymized" size="small" color="warning" variant="outlined" />
                  </ListItem>
                </List>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Grace Period:</strong> {summary.gracePeriodDays} days
                  </Typography>
                  <Typography variant="body2">
                    You can cancel the deletion request within this period by logging in again.
                  </Typography>
                </Alert>
              </Box>
            ) : (
              <Alert severity="error">Failed to load deletion summary.</Alert>
            )}
          </Box>
        )}

        {deletionStep === 'confirm' && (
          <Box>
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="subtitle2">
                This action will schedule your account for permanent deletion.
              </Typography>
            </Alert>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Enter your password to confirm account deletion. You will be logged out
              immediately.
            </Typography>

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={requestDeletionMutation.isError}
              helperText={
                requestDeletionMutation.isError
                  ? requestDeletionMutation.error?.message || 'Incorrect password'
                  : ''
              }
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Reason for leaving (optional)"
              multiline
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Help us improve by sharing why you're deleting your account..."
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {deletionStep === 'check' && canDelete && (
          <Button onClick={handleProceedToSummary} color="error">
            Continue
          </Button>
        )}
        {deletionStep === 'summary' && (
          <Button onClick={handleProceedToConfirm} color="error">
            I Understand, Continue
          </Button>
        )}
        {deletionStep === 'confirm' && (
          <Button
            onClick={handleRequestDeletion}
            color="error"
            variant="contained"
            disabled={!password || requestDeletionMutation.isPending}
            startIcon={
              requestDeletionMutation.isPending ? (
                <CircularProgress size={16} />
              ) : (
                <DeleteIcon />
              )
            }
          >
            {requestDeletionMutation.isPending ? 'Deleting...' : 'Delete My Account'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
